import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

export const TrackedKeywordSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  keyword: z.string(),
  target_url: z.string().nullable(),
  tracked_since: IsoDateTimeSchema,
  last_checked_at: IsoDateTimeSchema.nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
  /**
   * Phase 1 keyword-picker — set when the tracked keyword was picked from a
   * keyword run rather than typed as free text. Null on pre-migration rows
   * and free-text entries.
   */
  keyword_id: UuidSchema.nullable().optional(),
  keyword_run_id: UuidSchema.nullable().optional(),
  /** Present on list-with-ranks responses. */
  current_position: z.number().int().nullable().optional(),
  /** Position from the check immediately before the latest one (per-check comparison). */
  previous_position: z.number().int().nullable().optional(),
  /** Timestamp of that previous check — useful for "vs 2 days ago" tooltips. */
  previous_checked_at: IsoDateTimeSchema.nullable().optional(),
  /** Signed delta of previous → current (positive = moved up). */
  change_since_last_check: z.number().int().nullable().optional(),
  change_7d: z.number().int().nullable().optional(),
  change_30d: z.number().int().nullable().optional(),
  url_that_ranked: z.string().nullable().optional(),
  last_provider: z.string().nullable().optional(),
  /** KeywordPicker linkage — metadata joined from the source keyword row when tracked via picker. Null for free-text entries. */
  intent: z.string().nullable().optional(),
  volume: z.number().int().nullable().optional(),
  difficulty: z.number().int().nullable().optional(),
});
export type TrackedKeyword = z.infer<typeof TrackedKeywordSchema>;

export const CreateTrackedKeywordRequestSchema = z
  .object({
    project_id: UuidSchema,
    keyword: z.string().trim().min(1).max(200),
    /** Phase 1 keyword-picker — set when picked from a keyword run. */
    keyword_id: UuidSchema.optional(),
    keyword_run_id: UuidSchema.optional(),
    target_url: z
      .string()
      .trim()
      .url()
      .max(2000)
      .optional()
      .nullable(),
  })
  .refine((v) => !v.keyword_id || v.keyword_run_id, {
    message: 'keyword_run_id required when keyword_id is provided',
    path: ['keyword_run_id'],
  });
export type CreateTrackedKeywordRequest = z.infer<typeof CreateTrackedKeywordRequestSchema>;

/**
 * Phase 1 bulk-track endpoint — POST /v1/tracked-keywords/bulk. Used by the
 * "Track selected" flow on the keyword-run detail page. Each item is validated
 * independently server-side; integrity failures are surfaced as skipped items
 * rather than aborting the whole batch.
 */
export const CreateTrackedKeywordsBulkItemSchema = z
  .object({
    keyword: z.string().trim().min(1).max(200),
    keyword_id: UuidSchema.optional(),
    keyword_run_id: UuidSchema.optional(),
    target_url: z
      .string()
      .trim()
      .url()
      .max(2000)
      .optional()
      .nullable(),
  })
  .refine((v) => !v.keyword_id || v.keyword_run_id, {
    message: 'keyword_run_id required when keyword_id is provided',
    path: ['keyword_run_id'],
  });
export type CreateTrackedKeywordsBulkItem = z.infer<typeof CreateTrackedKeywordsBulkItemSchema>;

export const CreateTrackedKeywordsBulkRequestSchema = z.object({
  project_id: UuidSchema,
  items: z.array(CreateTrackedKeywordsBulkItemSchema).min(1).max(100),
});
export type CreateTrackedKeywordsBulkRequest = z.infer<
  typeof CreateTrackedKeywordsBulkRequestSchema
>;

export const TrackedKeywordsBulkSkipReasonSchema = z.enum([
  'already_tracked',
  'keyword_reference_invalid',
  'over_limit',
]);
export type TrackedKeywordsBulkSkipReason = z.infer<typeof TrackedKeywordsBulkSkipReasonSchema>;

export const CreateTrackedKeywordsBulkResponseSchema = z.object({
  created: z.array(TrackedKeywordSchema),
  skipped: z.array(
    z.object({
      keyword: z.string(),
      reason: TrackedKeywordsBulkSkipReasonSchema,
    }),
  ),
});
export type CreateTrackedKeywordsBulkResponse = z.infer<
  typeof CreateTrackedKeywordsBulkResponseSchema
>;

export const RankHistorySchema = z.object({
  id: UuidSchema,
  tracked_keyword_id: UuidSchema,
  checked_at: IsoDateTimeSchema,
  position: z.number().int().nullable(),
  url_that_ranked: z.string().nullable(),
  serp_features: z.unknown(),
  provider: z.string(),
});
export type RankHistory = z.infer<typeof RankHistorySchema>;

export const RankDropAlertSchema = z.object({
  tracked_keyword_id: UuidSchema,
  keyword: z.string(),
  current_position: z.number().int().nullable(),
  previous_position: z.number().int().nullable(),
  drop: z.number().int(),
});
export type RankDropAlert = z.infer<typeof RankDropAlertSchema>;

export const RankSummarySchema = z.object({
  tracked_count: z.number().int(),
  moved_up: z.number().int(),
  moved_down: z.number().int(),
  unchanged: z.number().int(),
  not_ranking: z.number().int(),
  drop_alerts: z.array(RankDropAlertSchema),
});
export type RankSummary = z.infer<typeof RankSummarySchema>;

export const CreateRankCheckRequestSchema = z.object({
  project_id: UuidSchema,
  /** When set, only this keyword is checked (single-keyword auto-trigger after tracking). */
  tracked_keyword_id: UuidSchema.optional(),
});
export type CreateRankCheckRequest = z.infer<typeof CreateRankCheckRequestSchema>;
